/**
 * Fire-and-forget order-trail comment inserts for automated audit events.
 * @module orderTrailComment
 */

import { singleOrderDetailsGenericInsert } from "../../shared/api/postgreSQL/postCalls/singleOrderDetailsGenericInsert";
import {
  UNASSIGNED_TEXT,
  UNASSIGNED_TEXT_LABEL,
} from "../../shared/constants/stringLiterals";
import { AuthStatusOptionModel } from "../data-model/authStatusOptions";
import { BOOptionModel } from "../data-model/boOptionModel";
import {
  AuthOnFile,
  AuthRangeResponse,
  JCodeDetails,
  JCodeDocuments,
} from "../data-model/medicalPaOrdersModel";
import { SingleOrderDetailsTableName } from "../../shared/enums/singleOrderDetailsTableName";
import { StatusNoteType } from "../../shared/enums/statusNoteType";
import { logError } from "./customLogger";

/** Shared follow-up type applied to every automated order-trail comment. */
export const ORDER_TRAIL_EVENT_TRIGGERED = {
  id: "event_triggered",
  label: "Triggered an Event",
} as const;

/** Fallback label when a from/to value is missing in comment builders. */
const UNKNOWN_VALUE = "Unknown";

/** Order context required for order-trail inserts (e.g. bulk assign). */
export interface OrderTrailOrderContext {
  /** PA order identifier. */
  orderId: string;
  /** Patient MRN / ID required by the comments table. */
  patientId: string;
  /** Previous assignee display name when available. */
  fromAssignee?: string;
}

/**
 * Resolves patient_id for order-trail inserts from order payload/demographics.
 *
 * @input payloadPatientId - Patient ID on the order payload
 * @input demographicsPatientId - Patient ID on demographics
 * @input demographicsPatientMrn - Patient MRN on demographics
 * @return Patient identifier string (may be empty)
 */
export const resolvePatientIdForOrderTrail = (
  payloadPatientId?: string,
  demographicsPatientId?: string,
  demographicsPatientMrn?: string,
): string =>
  payloadPatientId?.trim() ||
  demographicsPatientId?.trim() ||
  demographicsPatientMrn?.trim() ||
  "";

/**
 * Builds {@link OrderTrailOrderContext} from a worklist row's serialized row data.
 *
 * @input orderId - PA order ID for the selected row
 * @input rowDataJson - JSON string stored on the table row
 * @return Order and patient context for trail comment inserts
 */
export const extractOrderTrailContextFromRowData = (
  orderId: string,
  rowDataJson?: string,
): OrderTrailOrderContext => {
  if (!rowDataJson) {
    return { orderId, patientId: "" };
  }

  try {
    const parsed = JSON.parse(rowDataJson) as {
      patient_id?: string;
      assigned_to_name?: string;
      assigned_to?:
        | string
        | {
            assigned_to_id?: string;
            assigned_to_name?: string;
            provider_id?: string;
            provider_name?: string;
          };
      payload?: { patient_id?: string };
      demographics?: { patient_id?: string; patient_mrn?: string };
    };

    const assignedToId =
      typeof parsed.assigned_to === "string"
        ? parsed.assigned_to
        : (parsed.assigned_to?.assigned_to_id ??
          parsed.assigned_to?.provider_id);

    const assignedToName =
      typeof parsed.assigned_to === "object" && parsed.assigned_to
        ? (parsed.assigned_to.provider_name ??
          parsed.assigned_to.assigned_to_name)
        : parsed.assigned_to_name;

    return {
      orderId,
      patientId: resolvePatientIdForOrderTrail(
        parsed.payload?.patient_id ?? parsed.patient_id,
        parsed.demographics?.patient_id,
        parsed.demographics?.patient_mrn ?? parsed.patient_id,
      ),
      fromAssignee: getAssigneeDisplayName(assignedToId, assignedToName),
    };
  } catch {
    return { orderId, patientId: "" };
  }
};

/**
 * Resolves a human-readable assignee name from order assignment fields.
 *
 * @input assignedToId - Provider ID or unassigned sentinel
 * @input assignedToName - Display name stored on the order
 * @return Label suitable for order-trail from/to comment builders
 */
export const getAssigneeDisplayName = (
  assignedToId?: string,
  assignedToName?: string,
): string => {
  if (!assignedToId || assignedToId === UNASSIGNED_TEXT) {
    return UNASSIGNED_TEXT_LABEL;
  }

  return assignedToName?.trim() || UNKNOWN_VALUE;
};

/**
 * Whether the order had a known previous assignee before this change.
 *
 * @input fromAssignee - Resolved previous assignee label
 * @return True when a meaningful prior assignee exists for from/to wording
 */
const hasPreviousAssignee = (fromAssignee: string): boolean => {
  const trimmed = fromAssignee.trim();
  if (!trimmed) {
    return false;
  }

  return trimmed !== UNKNOWN_VALUE && trimmed !== UNASSIGNED_TEXT_LABEL;
};

/**
 * Builds order-trail comment text for assignment changes.
 *
 * When there is no prior assignee (unassigned, unknown, or empty), uses
 * "Order assigned to {name}." Otherwise uses from/to wording.
 *
 * @input fromAssignee - Previous assignee display name
 * @input toAssignee - New assignee display name
 * @return Formatted comment string
 */
export const buildOrderAssignedReassignedComment = (
  fromAssignee: string,
  toAssignee: string,
): string => {
  const to = toAssignee.trim() || UNKNOWN_VALUE;

  if (!hasPreviousAssignee(fromAssignee)) {
    return `Order assigned to ${to}.`;
  }

  return `Order Reassigned from ${fromAssignee.trim()} to ${to}.`;
};

/**
 * Builds order-trail comment text when drug/auth status is flipped.
 *
 * @input fromStatus - Previous status label
 * @input toStatus - New status label
 * @return Formatted comment string
 */
export const buildDrugStatusFlippedComment = (
  fromStatus: string,
  toStatus: string,
): string =>
  `Drug Status Flipped from ${fromStatus || UNKNOWN_VALUE} to ${toStatus || UNKNOWN_VALUE}.`;

/**
 * Resolves a human-readable auth status label from a status id.
 *
 * @input statusId - Auth status option id stored on the order
 * @input authStatusOptions - Payer-specific auth status dropdown options
 * @return Display label, or the raw id when no option matches
 */
export const resolveAuthStatusLabel = (
  statusId: string | undefined,
  authStatusOptions: AuthStatusOptionModel[],
): string => {
  if (!statusId?.trim()) {
    return UNKNOWN_VALUE;
  }

  const match = authStatusOptions.find((option) => option.id === statusId);
  return match?.text ?? statusId;
};

/**
 * Reads persisted auth_status from AuthOnFile or a nested auth_on_file wrapper.
 *
 * @input authOnFile - Standalone auth-on-file payload or accumulator response
 * @return Persisted auth status id when present
 */
export const resolvePersistedAuthStatusId = (
  authOnFile?: AuthOnFile | { auth_on_file?: AuthOnFile } | null,
): string | undefined => {
  if (!authOnFile) {
    return undefined;
  }

  if ("auth_on_file" in authOnFile && authOnFile.auth_on_file?.auth_status) {
    return authOnFile.auth_on_file.auth_status;
  }

  return (authOnFile as AuthOnFile).auth_status;
};

/**
 * Inserts an order-trail comment when drug/auth status changes (fire-and-forget).
 *
 * Skips the insert when `toStatusId` is empty or equals `fromStatusId`.
 *
 * @input params - Order context, provider identity, status ids, and option list
 */
export const insertDrugStatusFlippedTrailCommentIfChanged = (
  params: Omit<InsertOrderTrailCommentParams, "comments"> & {
    fromStatusId?: string;
    toStatusId?: string;
    authStatusOptions: AuthStatusOptionModel[];
  },
): void => {
  const { fromStatusId, toStatusId, authStatusOptions, ...rest } = params;

  if (!toStatusId?.trim()) {
    return;
  }

  const normalizedFrom = fromStatusId?.trim() ?? "";
  const normalizedTo = toStatusId.trim();

  if (normalizedFrom === normalizedTo) {
    return;
  }

  insertOrderTrailComment({
    ...rest,
    comments: buildDrugStatusFlippedComment(
      resolveAuthStatusLabel(normalizedFrom || undefined, authStatusOptions),
      resolveAuthStatusLabel(normalizedTo, authStatusOptions),
    ),
  });
};

/**
 * Resolves a human-readable BO status label from a stored value.
 *
 * @input statusValue - BO status value on the order or report
 * @input boOptions - Organisation BO dropdown options
 * @return Display label, or the raw value when no option matches
 */
export const resolveBoStatusLabel = (
  statusValue: string | undefined,
  boOptions: BOOptionModel[],
): string => {
  if (!statusValue?.trim()) {
    return UNKNOWN_VALUE;
  }

  return (
    boOptions.find((option) => option.value === statusValue)?.label ??
    statusValue
  );
};

/**
 * Builds order-trail comment text for OncoEMR comment writeback.
 *
 * When before/after BO status values are the same, uses unchanged-status
 * wording instead of redundant "from X to X" phrasing.
 *
 * @input fromBoStatus - BO status value on the order before writeback
 * @input toBoStatus - BO status value sent in the writeback payload
 * @input boOptions - Organisation BO dropdown options for label resolution
 * @return Formatted comment string
 */
export const buildCommentWritebackOncoemrComment = (
  fromBoStatus: string | undefined,
  toBoStatus: string | undefined,
  boOptions: BOOptionModel[],
): string => {
  const normalizedFrom = fromBoStatus?.trim() ?? "";
  const normalizedTo = toBoStatus?.trim() ?? "";

  if (normalizedFrom === normalizedTo) {
    const statusLabel = resolveBoStatusLabel(
      normalizedTo || normalizedFrom || undefined,
      boOptions,
    );
    return `Comment Writeback to OncoEMR (BO status unchanged: ${statusLabel}).`;
  }

  return `Comment Writeback to OncoEMR from ${resolveBoStatusLabel(fromBoStatus, boOptions)} to ${resolveBoStatusLabel(toBoStatus, boOptions)}.`;
};

/**
 * Inserts an order-trail comment after OncoEMR comment writeback (fire-and-forget).
 *
 * Comment text uses from/to wording when BO status changes; otherwise records
 * unchanged status via {@link buildCommentWritebackOncoemrComment}.
 *
 * @input params - Order context, provider identity, and BO status before/after
 */
export const insertCommentWritebackOncoemrTrailComment = (
  params: Omit<InsertOrderTrailCommentParams, "comments"> & {
    fromBoStatus?: string;
    toBoStatus?: string;
    boOptions: BOOptionModel[];
  },
): void => {
  const { fromBoStatus, toBoStatus, boOptions, ...rest } = params;

  insertOrderTrailComment({
    ...rest,
    comments: buildCommentWritebackOncoemrComment(
      fromBoStatus,
      toBoStatus,
      boOptions,
    ),
  });
};

/**
 * Flattens J-code rows from auth-on-file entries for label resolution.
 *
 * @input authEntries - Auth-on-file entry list
 * @return All J-code detail rows across entries
 */
const flattenJCodesFromAuthEntries = (
  authEntries: AuthRangeResponse[],
): JCodeDetails[] => authEntries.flatMap((entry) => entry.j_codes ?? []);

/**
 * Resolves user-facing J-code labels (e.g. J9035) from internal identifiers.
 *
 * @input jCodeIds - J-code ids selected for upload/delete
 * @input authEntries - Auth-on-file entries used to map id → j_code
 * @return Display labels in the same order as unique resolved codes
 */
export const resolveJCodeLabelsForTrail = (
  jCodeIds: string[],
  authEntries?: AuthRangeResponse[],
): string[] => {
  const flatJCodes = flattenJCodesFromAuthEntries(authEntries ?? []);

  const labels = jCodeIds
    .map((id) => {
      const trimmedId = id.trim();
      if (!trimmedId) {
        return "";
      }

      const match = flatJCodes.find(
        (jCode) => jCode?.id === trimmedId || jCode?.j_code === trimmedId,
      );

      return match?.j_code ?? match?.id ?? trimmedId;
    })
    .filter(Boolean);

  return [...new Set(labels)];
};

/**
 * Resolves J-code labels for documents selected for OncoEMR RPA upload.
 *
 * @input selectedDocuments - Documents chosen in the trigger-RPA modal
 * @input jcodes - Flat J-code list for the order
 * @return Display labels for J-codes that own the selected document paths
 */
export const resolveJCodeLabelsForSelectedDocuments = (
  selectedDocuments: JCodeDocuments[],
  jcodes?: JCodeDetails[],
): string[] => {
  if (!jcodes?.length || selectedDocuments.length === 0) {
    return [];
  }

  const selectedPaths = new Set(
    selectedDocuments.map((doc) => doc.doc_path).filter(Boolean),
  );

  const affectedIds = jcodes
    .filter((jCode) =>
      (jCode.documents ?? []).some(
        (doc) => doc.doc_path && selectedPaths.has(doc.doc_path),
      ),
    )
    .map((jCode) => jCode.id ?? jCode.j_code ?? "")
    .filter(Boolean);

  return resolveJCodeLabelsForTrail(affectedIds, [{ j_codes: jcodes }]);
};

/**
 * Formats one or more J-code labels for trail comment text.
 *
 * @input jCodeLabels - Human-readable J-code strings
 * @return Comma/and-separated label string, or empty when none
 */
export const formatJCodesForTrailComment = (jCodeLabels: string[]): string => {
  const unique = [...new Set(jCodeLabels.filter((label) => label.trim()))];
  if (unique.length === 0) {
    return "";
  }

  if (unique.length === 1) {
    return unique[0];
  }

  if (unique.length === 2) {
    return `${unique[0]} and ${unique[1]}`;
  }

  return `${unique.slice(0, -1).join(", ")}, and ${unique[unique.length - 1]}`;
};

/**
 * Builds the detail clause for document upload/delete trail comments.
 *
 * @input documentLabel - Human-readable document type (e.g. NAR letter)
 * @input jCodeLabels - J-code(s) affected
 * @return Detail suffix after the event prefix, or empty when neither is set
 */
const buildDocumentOrderTrailDetail = (
  documentLabel?: string,
  jCodeLabels: string[] = [],
): string => {
  const doc = documentLabel?.trim() ?? "";
  const jCodes = formatJCodesForTrailComment(jCodeLabels);

  if (doc && jCodes) {
    return `${doc} for ${jCodes}`;
  }

  if (doc) {
    return doc;
  }

  if (jCodes) {
    return `for ${jCodes}`;
  }

  return "";
};

/**
 * Builds order-trail comment text when a document is uploaded to the order.
 *
 * @input jCodeLabels - J-code(s) the document was attached to
 * @input documentLabel - Human-readable document type (e.g. NAR letter)
 * @return Formatted comment string
 */
export const buildDocumentUploadedToOrderComment = (
  jCodeLabels: string[] = [],
  documentLabel?: string,
): string => {
  const detail = buildDocumentOrderTrailDetail(documentLabel, jCodeLabels);
  if (!detail) {
    return "Document Uploaded to Order.";
  }

  return `Document Uploaded to Order: ${detail}.`;
};

/**
 * Inserts an order-trail comment when a document is uploaded to the order (fire-and-forget).
 *
 * @input params - Order context, provider identity, document type, and J-code labels
 */
export const insertDocumentUploadedToOrderTrailComment = (
  params: Omit<InsertOrderTrailCommentParams, "comments"> & {
    jCodeLabels?: string[];
    documentLabel?: string;
  },
): void => {
  const { jCodeLabels, documentLabel, ...rest } = params;

  insertOrderTrailComment({
    ...rest,
    comments: buildDocumentUploadedToOrderComment(jCodeLabels, documentLabel),
  });
};

/**
 * Builds order-trail comment text when a document is deleted from the order.
 *
 * @input jCodeLabels - J-code(s) the document was removed from
 * @input documentLabel - Human-readable document type (e.g. Auth letter)
 * @return Formatted comment string
 */
export const buildDocumentDeletedFromOrderComment = (
  jCodeLabels: string[] = [],
  documentLabel?: string,
): string => {
  const detail = buildDocumentOrderTrailDetail(documentLabel, jCodeLabels);
  if (!detail) {
    return "Document Deleted from Order.";
  }

  return `Document Deleted from Order: ${detail}.`;
};

/**
 * Inserts an order-trail comment when a document is deleted from the order (fire-and-forget).
 *
 * @input params - Order context, provider identity, document type, and J-code labels
 */
export const insertDocumentDeletedFromOrderTrailComment = (
  params: Omit<InsertOrderTrailCommentParams, "comments"> & {
    jCodeLabels?: string[];
    documentLabel?: string;
  },
): void => {
  const { jCodeLabels, documentLabel, ...rest } = params;

  insertOrderTrailComment({
    ...rest,
    comments: buildDocumentDeletedFromOrderComment(jCodeLabels, documentLabel),
  });
};

/**
 * Builds order-trail comment text when a document is uploaded to OncoEMR.
 *
 * @input jCodeLabels - J-code(s) the document was uploaded for
 * @input documentLabel - Human-readable document name or type
 * @return Formatted comment string
 */
export const buildDocumentUploadedToOncoemrComment = (
  jCodeLabels: string[] = [],
  documentLabel?: string,
): string => {
  const detail = buildDocumentOrderTrailDetail(documentLabel, jCodeLabels);
  if (!detail) {
    return "Document Uploaded to OncoEMR.";
  }

  return `Document Uploaded to OncoEMR: ${detail}.`;
};

/**
 * Inserts an order-trail comment when a document is uploaded to OncoEMR (fire-and-forget).
 *
 * @input params - Order context, provider identity, document detail, and J-code labels
 */
export const insertDocumentUploadedToOncoemrTrailComment = (
  params: Omit<InsertOrderTrailCommentParams, "comments"> & {
    jCodeLabels?: string[];
    documentLabel?: string;
  },
): void => {
  const { jCodeLabels, documentLabel, ...rest } = params;

  insertOrderTrailComment({
    ...rest,
    comments: buildDocumentUploadedToOncoemrComment(jCodeLabels, documentLabel),
  });
};

/**
 * Builds order-trail comment text when master auth status changes.
 *
 * @input fromStatus - Previous master auth status label
 * @input toStatus - New master auth status label
 * @return Formatted comment string
 */
export const buildMasterAuthStatusShiftedComment = (
  fromStatus: string,
  toStatus: string,
): string =>
  `Master Auth Status Shifted from ${fromStatus || UNKNOWN_VALUE} to ${toStatus || UNKNOWN_VALUE}.`;

/**
 * Inserts an order-trail comment when master auth status changes (fire-and-forget).
 *
 * Skips the insert when `toStatusId` is empty or equals `fromStatusId`.
 *
 * @input params - Order context, provider identity, status ids, and option list
 */
export const insertMasterAuthStatusShiftedTrailCommentIfChanged = (
  params: Omit<InsertOrderTrailCommentParams, "comments"> & {
    fromStatusId?: string;
    toStatusId?: string;
    authStatusOptions: AuthStatusOptionModel[];
  },
): void => {
  const { fromStatusId, toStatusId, authStatusOptions, ...rest } = params;

  if (!toStatusId?.trim()) {
    return;
  }

  const normalizedFrom = fromStatusId?.trim() ?? "";
  const normalizedTo = toStatusId.trim();

  if (normalizedFrom === normalizedTo) {
    return;
  }

  insertOrderTrailComment({
    ...rest,
    comments: buildMasterAuthStatusShiftedComment(
      resolveAuthStatusLabel(normalizedFrom || undefined, authStatusOptions),
      resolveAuthStatusLabel(normalizedTo, authStatusOptions),
    ),
  });
};

/**
 * Builds order-trail comment text when normal BO notes are saved.
 *
 * @input noteCount - Number of notes added or updated
 * @return Formatted comment string
 */
export const buildNormalNotesAddedComment = (noteCount: number): string =>
  `Normal Notes Added: ${noteCount} note${noteCount === 1 ? "" : "s"}.`;

/**
 * Inserts an order-trail comment when normal BO notes are saved (fire-and-forget).
 *
 * Skips the insert when `noteCount` is zero or negative.
 *
 * @input params - Order context, provider identity, and number of notes saved
 */
export const insertNormalNotesAddedTrailComment = (
  params: Omit<InsertOrderTrailCommentParams, "comments"> & {
    noteCount: number;
  },
): void => {
  const { noteCount, ...rest } = params;

  if (noteCount <= 0) {
    return;
  }

  insertOrderTrailComment({
    ...rest,
    comments: buildNormalNotesAddedComment(noteCount),
  });
};

/**
 * Inserts an order-trail comment for an assignment change (fire-and-forget).
 *
 * Skips the insert when `fromAssignee` and `toAssignee` are the same.
 *
 * @input params - Order context, provider identity, and from/to assignee names
 */
export const insertOrderAssignedReassignedTrailComment = (
  params: Omit<InsertOrderTrailCommentParams, "comments"> & {
    fromAssignee: string;
    toAssignee: string;
  },
): void => {
  const { fromAssignee, toAssignee, ...rest } = params;
  if (fromAssignee === toAssignee) {
    return;
  }

  insertOrderTrailComment({
    ...rest,
    comments: buildOrderAssignedReassignedComment(fromAssignee, toAssignee),
  });
};

/** Parameters for {@link insertOrderTrailComment}. */
export interface InsertOrderTrailCommentParams {
  orderId: string;
  orgId: string;
  patientId?: string;
  providerEmail: string;
  providerName: string;
  comments: string;
}

/**
 * Inserts an automated order-trail comment without blocking the caller.
 *
 * All entries share the "Triggered an Event" follow-up type; event-specific
 * detail lives in `comments`. Failures are logged and swallowed (fire-and-forget).
 *
 * @input params - Order context, provider identity, and comment body
 */
export const insertOrderTrailComment = (
  params: InsertOrderTrailCommentParams,
): void => {
  const { orderId, orgId, patientId, providerEmail, providerName, comments } =
    params;

  void singleOrderDetailsGenericInsert({
    order_id: orderId,
    org_id: orgId,
    table_name: SingleOrderDetailsTableName.ORDER_COMMENTS,
    data: {
      follow_up_type_id: ORDER_TRAIL_EVENT_TRIGGERED.id,
      follow_up_type_label: ORDER_TRAIL_EVENT_TRIGGERED.label,
      comments: comments.trim(),
      note_type: StatusNoteType.OTHER,
      provider_email: providerEmail,
      provider_name: providerName || "Unknown Provider",
      notes_color: "",
      patient_id: patientId ?? "",
    },
  }).catch((error: unknown) => {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      "Order trail comment insert failed",
    );
  });
};
